import type { ConfigurationSet } from "@core-ts/Config"
import type { AuthenticationServiceClient } from "@core-ts/clients/AuthenticationServiceClient"
import type { StorageServiceClient } from "@core-ts/clients/StorageServiceClient"
import { AccessDeniedError, StandardError } from "../StandardError"
import type { Application } from "./Application"
import type { Instance } from "./Instance"
import { Log, type LogLevel } from "./Log"
import type { Request } from "./Request"
import { UnverifiedSession } from "./UnverifiedSession"
import { VerifiedSession } from "./VerifiedSession"
import { InstanceCache } from "@core-ts/Cache/InstanceCache"
import { LogServiceClient } from "@core-ts/clients/LogServiceClient"

export type LogMeta = {
    application: string
    request?: string
    instance?: string
    userId?: string
    userEmail?: string
    internalSerial?: string
}

export class Context {
    constructor(readonly application: Application<Instance>) {}

    clone(): Context {
        return new Context(this.application)
    }

    // Unique per instance
    get instance(): Instance {
        return this.implementGetter("instance") as Instance
    }
    set instance(instance: Instance) {
        this.implementSetter("instance", instance)
    }
    get haveInstance(): boolean {
        return this.implementHave("instance")
    }

    // Unique per instance
    get storage(): StorageServiceClient {
        return this.implementGetter("storage") as StorageServiceClient
    }
    set storage(storage: StorageServiceClient) {
        this.implementSetter("storage", storage)
    }
    get haveStorage(): boolean {
        return this.implementHave("storage")
    }

    // Unique per instance
    get logger(): LogServiceClient {
        return this.implementGetter("logger") as LogServiceClient
    }
    set logger(logger: LogServiceClient) {
        this.implementSetter("logger", logger)
    }
    get haveLogger(): boolean {
        return this.implementHave("logger")
    }

    // Unique per instance
    get authentication(): AuthenticationServiceClient {
        return this.implementGetter("authentication") as AuthenticationServiceClient
    }
    set authentication(authentication: AuthenticationServiceClient) {
        this.implementSetter("authentication", authentication)
    }
    get haveAuthentication(): boolean {
        return this.implementHave("authentication")
    }

    // Unique per instance
    get config(): ConfigurationSet {
        return this.implementGetter("config") as ConfigurationSet
    }
    set config(config: ConfigurationSet) {
        this.implementSetter("config", config)
    }
    get haveConfig(): boolean {
        return this.implementHave("config")
    }

    // Unique per request
    get request(): Request {
        return this.implementGetter("request") as Request
    }
    set request(request: Request) {
        this.implementSetter("request", request)
    }
    get haveRequest(): boolean {
        return this.implementHave("request")
    }
    // We allow the request to be cleared since we sometimes want to go from a job request to a more specific request
    clearRequest(): void {
        this.application.contextProvider.set("request", undefined)
    }

    get unverifiedSession(): UnverifiedSession {
        return this.implementGetter("unverifiedSession")
    }
    set unverifiedSession(unverifiedSession: UnverifiedSession) {
        // TODO: Why doesn't the logic below work?
        if (this.haveUnverifiedSession) {
            // In some cases a logged in user can call a function that logs in using an API key set in the config
            // This is allowed and overwrites the user session but we must reset the verified session data if there is any
            this.unsetProperty("verifiedSession")
            this.unsetProperty("unverifiedSession")
        }
        this.implementSetter("unverifiedSession", unverifiedSession)
    }
    get haveUnverifiedSession(): boolean {
        return this.implementHave("unverifiedSession")
    }

    get verifiedSession(): VerifiedSession {
        return this.implementGetter("verifiedSession")
    }
    set verifiedSession(verifiedSession: VerifiedSession) {
        this.implementSetter("verifiedSession", verifiedSession)
    }
    get haveVerifiedSession(): boolean {
        return this.implementHave("verifiedSession")
    }

    async verifySession(): Promise<void> {
        const verifiedSession = this.getOrUndefined("verifiedSession")

        if (verifiedSession === undefined) {
            const response = new VerifiedSession(await this.authentication.VerifySession())

            if (response === undefined || response === null || !response.userInfo)
                throw new AccessDeniedError("No valid session")

            this.verifiedSession = new VerifiedSession(response.userInfo)
        }
    }

    private loginSessionCache = new InstanceCache<UnverifiedSession>("LoginSession", 180) // Session should last at least 300 seconds so keep it for 180

    // If apiKey is undefined, it will use the apiKey from the config
    async loginApiKey(apiKeyParam?: string): Promise<void> {
        // TODO: Why "session" here? Shouldn't it be "unverifiedSession"?
        // And it probably shouldn't check, it should probably overwrite the current session if there is one
        const session = this.getOrUndefined("session")

        if (session === undefined) {
            const apiKey = apiKeyParam !== undefined ? apiKeyParam : this.config.getString("apiKey")

            const response = await this.loginSessionCache.get(apiKey, async () => {
                return new UnverifiedSession(await this.authentication.LoginApiKey(apiKey, "UTC"))
            })

            if (response === undefined || response === null) throw new AccessDeniedError("No valid session")

            context.unverifiedSession = response
        }
    }

    async createLocalContext(fnCallback: () => Promise<void>): Promise<void> {
        await this.application.contextProvider.create(fnCallback)
    }

    public dumpContents(where: string): void {
        let dump = `Context ${where} has: `
        for (const parameter of ["instance", "request", "config", "unverifiedSession", "verifiedSession"]) {
            const value = this.application.contextProvider.get(parameter)
            if (value !== undefined) dump += `${parameter} `
        }
        log.debug(dump)
    }

    private implementGetter<T>(name: string): T {
        const existingInstance = this.application.contextProvider.get(name)
        if (existingInstance === undefined) {
            throw new StandardError(`context.${name} is not set`)
        }

        return existingInstance as T
    }

    private implementHave<T>(name: string): boolean {
        const existingInstance = this.application.contextProvider.get(name)
        if (existingInstance === undefined) return false
        return true
    }

    private getOrUndefined<T>(name: string): T | undefined {
        return this.application.contextProvider.get(name) as T | undefined
    }

    private implementSetter<T>(name: string, value: T): void {
        const existingInstance = this.application.contextProvider.get(name)
        if (existingInstance !== undefined) throw new StandardError(`Cannot set context.${name}, can only be set once`)

        this.application.contextProvider.set(name, value)
    }

    private unsetProperty<T>(name: string): void {
        const existingInstance = this.application.contextProvider.get(name)
        if (existingInstance === undefined) return
        this.application.contextProvider.unset(name)
    }

    public getLogMeta(): LogMeta {
        let userId: string | undefined
        let userEmail: string | undefined
        const verifiedSession = this.getOrUndefined<VerifiedSession>("verifiedSession")
        if (verifiedSession !== undefined) {
            userId = verifiedSession.userInfo.verifiedUserID.toString()
            userEmail = verifiedSession.userInfo.verifiedEmail
        }
        const request = this.getOrUndefined<Request>("request")

        return {
            application: this.application.applicationName,
            request: request?.requestId,
            instance: this.getOrUndefined<Instance>("instance")?.instanceName,
            userId,
            userEmail,
            internalSerial: request?.internalSerial,
        }
    }

    public log(level: LogLevel, message: string | unknown, e: Error | null = null): void {
        let row = ""

        const instance = this.getOrUndefined<Instance>("instance")

        if (level === "DEBUG" && this.application.isFrontend) {
            // We would want this to be part of the FeatureFlags class, but since Context is not dependant on core-frontend we have to roll our own little version here?
            const debugParam = new URLSearchParams(window.location.search).get("debug")

            if (debugParam === null) return
                
            if (typeof message !== "string") {
                console.log(message)
            }
        }
        if ((level === "DEBUG" || level === "ACCESS") && instance?.instanceName === "testinstance") {
            // Remove debug output for the tests for now since that overflows the server log
            return
        }

        // Date
        /*const now = new Date()
    
        const paddedMonth = String(now.getUTCMonth()).padStart(2, "0")
        const paddedDay = String(now.getUTCDay()).padStart(2, "0")
        const paddedHour = String(now.getUTCHours()).padStart(2, "0")
        const paddedMinute = String(now.getUTCMinutes()).padStart(2, "0")
        const paddedSecond = String(now.getUTCSeconds()).padStart(2, "0")
    
        const date = `${now.getUTCFullYear()}-${paddedMonth}-${paddedDay} ${paddedHour}:${paddedMinute}:${paddedSecond}`
       */
        // if(false) {
        //     row += date
        // }

        // Application name
        row += String(this.application.applicationName).padEnd(25, " ") + " "

        const request = this.getOrUndefined<Request>("request")

        // Request/Thread
        row += String(request?.requestId ?? "").padEnd(18, " ") + " "

        // Instance
        row += String(instance?.instanceName ?? "").padEnd(10, " ") + " "

        // User
        let userText = ""
        const verifiedSession = this.getOrUndefined<VerifiedSession>("verifiedSession")
        if (verifiedSession !== undefined) {
            userText = verifiedSession.userInfo.sessionNameForLogging

            userText = userText.slice(0, 48)
        }

        row += String(userText).padEnd(32, " ") + " "

        // Log level
        row += String(level).padEnd(5, " ") + " "

        // Prefix (e.g. CCSG#12345)
        row += String(request?.internalSerial ?? "").padEnd(30, " ") + " "

        if (typeof message === "string") row += message

        if (e !== null) row += " Causing exception: " + e.message + " " + e.name + " " + e.stack

        console.log(row)
        //  Log to file
        //    const jsonRow: LogRow = {date}
        //    JSON.stringify(jsonRow)
    }
}

export const log = Log

export abstract class ContextProvider {
    abstract create(fnCallback: () => Promise<void>): Promise<void>
    abstract get<T>(name: string): T | undefined
    abstract set<T>(name: string, value: T): void
    abstract unset<T>(name: string): void
}

export class ContextProviderNoContexts extends ContextProvider {
    async create(fnCallback: () => Promise<void>): Promise<void> {
        await fnCallback()
    }

    get<T>(name: string): T | undefined {
        return this.store.get(name) as T | undefined
    }

    set<T>(name: string, value: T): void {
        this.store.set(name, value)
    }

    unset(name: string): void {
        this.store.delete(name)
    }

    // Since the frontend doesn't support different contexts, we can just store these locally
    private store: Map<string, unknown> = new Map()
}

export let context: Context

export function setRootContext(newContext: Context): void {
    if (context !== undefined) throw new StandardError("Cannot set root context, root context can only be set once")
    context = newContext
}

// This is just for debugging of cls-hooked
export function printContextDebugOutput(name: string, expectedDepth: number | null = null): void {
    interface IS {
        instance?: Instance
    }
    interface NS {
        _set: IS[]
        active: IS
    }
    const namespace = (process as unknown as { namespaces: Record<string, NS> }).namespaces
    const firstNamespace = Object.values(namespace)[0]
    const depth = firstNamespace._set.length
    console.log(name + ": CLS-hooked Context on stack = " + depth)
    console.log("   active: instance = " + firstNamespace.active?.instance?.instanceName)
    for (let i = 0; i < depth; i++) {
        console.log("   _set[" + i + "]: instance = " + firstNamespace._set[i]?.instance?.instanceName)
    }
    if (expectedDepth !== null && depth !== expectedDepth) {
        console.error("   CLS-hooked Context depth is not as expected at " + name)
    }
}
